import { Link } from "react-router-dom";
import { useState, useEffect } from "react";

const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      title: {
        line1: "가슴 뛰는 오늘,",
        line2: "홀가분하게 떠나는",
        line3: "나만의 트레킹"
      },
      description: {
        line1: "리무진 버스로 편안하게, 전문 가이드와 안전하게",
        line2: "프리미엄 당일 트레킹 투어를 확인해보세요!"
      },
      cta: {
        text: "지금 코스 둘러보기",
        link: "/courses"
      },
      backgroundImage: "https://image.wholsee.kr/image1.webp"
    },
    {
      title: {
        line1: "한국 걷기 여행문화의",
        line2: "씨앗이 되는",
        line3: "홀씨 숙박"
      },
      description: {
        line1: "알베르게처럼 트레커를 위한 전문 숙박 정보",
        line2: "저렴한 가격, 필수 편의시설, 그리고 함께 만드는 여행문화"
      },
      cta: {
        text: "숙소 둘러보기",
        link: "/stay"
      },
      backgroundImage: "https://image.wholsee.kr/image3.webp"
    },
    {
      title: {
        line1: "한국의 걷기 여행문화",
        line2: "활성화의 씨앗",
        line3: "홀씨의 철학"
      },
      description: {
        line1: "여행자, 지역사회, 그리고 문화가",
        line2: "함께 성장하는 생태계를 만들어갑니다"
      },
      cta: {
        text: "홀씨 알아보기",
        link: "#brand-philosophy"
      },
      backgroundImage: "https://image.wholsee.kr/image10.webp"
    }
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Auto-advance slides
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 8000); // Change slide every 8 seconds

    return () => clearInterval(interval);
  }, [slides.length]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const handleCTAClick = (link: string) => {
    if (link.startsWith('#')) {
      const element = document.querySelector(link);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const currentSlideData = slides[currentSlide];

  return (
    <section
      data-testid="hero-section"
      className="relative h-[42vh] lg:h-[70vh] min-h-[300px] lg:min-h-[500px] flex items-center justify-center overflow-hidden"
    >
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000"
        style={{
          backgroundImage: `url('${currentSlideData.backgroundImage}')`
        }}
      >
        {/* Animated gradient overlay */}
        <div
          className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-black/50 to-orange-900/30 animate-gradient-x"
          style={{
            backgroundSize: '200% 200%'
          }}
        ></div>

        {/* Shimmer effect overlay */}
        <div className="absolute inset-0 overflow-hidden">
          <div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer"
            style={{
              width: '200%',
              left: '-100%'
            }}
          ></div>
        </div>
      </div>

      {/* Floating particles */}
      <div className="absolute inset-0">
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1.5 h-1.5 bg-white/15 rounded-full animate-float"
            style={{
              left: `${30 + i * 20}%`,
              top: `${40 + i % 2 * 20}%`,
              animationDelay: `${i * 1.5}s`,
              animationDuration: `${8 + i * 2}s`
            }}
          ></div>
        ))}
      </div>

      {/* Content with smooth transitions */}
      <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
        <h2
          key={`title-${currentSlide}`}
          className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 lg:mb-6 leading-tight animate-fadeIn"
          style={{
            lineHeight: '1.5'
          }}
        >
          <span
            className="inline-block text-shadow-lg"
            style={{
              textShadow: '0 0 20px rgba(255,255,255,0.3)'
            }}
          >
            {currentSlideData.title.line1}
          </span>
          <br />
          <span
            className="relative inline-block text-[#F39800]"
            style={{
              textShadow: '0 0 30px rgba(243, 152, 0, 0.5)'
            }}
          >
            {currentSlideData.title.line2}
            {/* Glowing underline */}
            <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#F39800] to-transparent animate-pulse-glow rounded-full"></div>
          </span>
          <br />
          <span
            className="inline-block"
            style={{
              textShadow: '0 0 20px rgba(255,255,255,0.3)'
            }}
          >
            {currentSlideData.title.line3}
          </span>
        </h2>

        <p
          key={`desc-${currentSlide}`}
          className="text-base md:text-lg lg:text-xl mb-8 lg:mb-12 text-gray-200 max-w-2xl mx-auto animate-fadeIn"
          style={{
            animationDelay: '0.2s'
          }}
        >
          <span
            className="inline-block"
            style={{
              textShadow: '0 0 10px rgba(255,255,255,0.2)'
            }}
          >
            {currentSlideData.description.line1}
          </span>
          <br />
          <span
            className="inline-block"
            style={{
              textShadow: '0 0 10px rgba(255,255,255,0.2)'
            }}
          >
            {currentSlideData.description.line2}
          </span>
        </p>

        <div key={`cta-${currentSlide}`} className="relative inline-block animate-fadeIn" style={{ animationDelay: '0.4s' }}>
          {currentSlideData.cta.link.startsWith('#') ? (
            <button
              onClick={() => handleCTAClick(currentSlideData.cta.link)}
              className="bg-primary hover:bg-primary-600 hover:text-white text-white px-8 py-3 lg:px-10 lg:py-4 rounded-xl lg:rounded-2xl text-base lg:text-lg font-bold
                transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-[#F39800]/50"
            >
              {currentSlideData.cta.text}
            </button>
          ) : (
            <Link
              to={currentSlideData.cta.link}
              className="bg-primary hover:bg-primary-600 hover:text-white text-white px-8 py-3 lg:px-10 lg:py-4 rounded-xl lg:rounded-2xl text-base lg:text-lg font-bold
                transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-[#F39800]/50"
            >
              {currentSlideData.cta.text}
            </Link>
          )}
        </div>
      </div>

      {/* Carousel indicators */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20">
        <div className="flex space-x-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                currentSlide === index
                  ? 'bg-[#F39800] w-8'
                  : 'bg-white/40 hover:bg-white/60'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
